import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { Button } from './ui/Button';

/**
 * Minimal fallback shown when the boot-time auth check couldn't reach the
 * server (flaky/interrupted connection, e.g. PWA cold start on mobile).
 * This is deliberately NOT a "you are logged out" screen — the user's
 * session may still be perfectly valid; we just couldn't verify it. Retry
 * is user-initiated only (no auto-retry loop, see AuthContext.retryAuthInit).
 *
 * Shared across all route guards (PrivateRoute, RequireSuperAdmin,
 * ChannelSelectPage) so a transient connectivity blip never forces a
 * redirect to /login while a valid session may still exist.
 */
export const ConnectionErrorScreen = () => {
  const { t } = useTranslation('common');
  const { retryAuthInit } = useAuth();
  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await retryAuthInit();
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-6">
      <div className="flex w-full max-w-[340px] flex-col items-center rounded-2xl border border-gray-200 bg-white px-7 pb-8 pt-9 text-center shadow-lg">
        {/* Icon halo with reduced-motion-safe pulsing ring */}
        <div className="relative mb-5 grid h-[76px] w-[76px] place-items-center rounded-full bg-primary-50">
          <span
            aria-hidden="true"
            className="motion-safe:animate-ring-pulse absolute -inset-1.5 rounded-full border-2 border-primary-400/45"
          />
          <svg
            className="relative h-9 w-9 text-primary-600"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.9}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M1 1l22 22" />
            <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
            <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
            <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
            <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
            <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
            <line x1="12" y1="20" x2="12.01" y2="20" />
          </svg>
        </div>

        <h1 className="font-wagner mb-2 text-xl font-bold leading-tight text-gray-900">
          {t('connection_error_title', { defaultValue: "Can't reach the server" })}
        </h1>
        <p className="mb-6 max-w-[30ch] text-sm leading-relaxed text-gray-600">
          {t('connection_error_message', {
            defaultValue:
              "You're still signed in. The connection dropped for a moment, so check your network and try again.",
          })}
        </p>

        <Button onClick={handleRetry} loading={isRetrying} className="min-w-[148px] gap-2">
          {!isRetrying && (
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M23 4v6h-6" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
          )}
          {t('retry', { defaultValue: 'Retry' })}
        </Button>

        <p className="mt-4 flex items-center gap-1.5 text-xs text-gray-400">
          <svg
            className="h-3.5 w-3.5 text-primary-400"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
          {t('connection_error_reassurance', { defaultValue: 'Nothing you were working on is lost.' })}
        </p>
      </div>
    </div>
  );
};
